/**
 * Site settings.
 *
 * This is where §26 of the brief lives: company identity, contact details, social profiles, SEO
 * defaults, analytics IDs, CTA labels and feature flags — all editable, none hardcoded. Fields the
 * source website never published are flagged so an administrator knows what is still missing.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Checkbox, FormField, Input, Spinner, Tabs, TabPanel, Textarea } from '@/components/ui';
import { LoadError } from '@/components/site/blocks';
import { cmsApi } from '@/services/api';
import { ApiError } from '@/services/client';

interface SettingField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'url' | 'textarea' | 'checkbox' | 'date';
  hint?: string;
  missingOnSource?: boolean;
}

const GROUPS: { key: string; label: string; description: string; fields: SettingField[] }[] = [
  {
    key: 'identity',
    label: 'Company',
    description: 'How the company is named and identified across the site.',
    fields: [
      { name: 'company_name', label: 'Legal company name', type: 'text' },
      { name: 'company_short_name', label: 'Short name', type: 'text', hint: 'Used in the header and compact contexts.' },
      { name: 'tagline', label: 'Tagline', type: 'text' },
      {
        name: 'registration_number',
        label: 'CIN / registration number',
        type: 'text',
        missingOnSource: true,
        hint: 'Shown in the footer. Not published on the previous website.',
      },
      { name: 'gstin', label: 'GSTIN', type: 'text', missingOnSource: true },
      { name: 'incorporated_on', label: 'Incorporated on', type: 'date', missingOnSource: true },
    ],
  },
  {
    key: 'contact',
    label: 'Contact',
    description: 'Published contact details and the addresses form notifications are routed to.',
    fields: [
      {
        name: 'primary_email',
        label: 'Primary email',
        type: 'email',
        missingOnSource: true,
        hint: 'The previous website published no email address at all. Add one so enquiries can be acknowledged.',
      },
      { name: 'sales_email', label: 'Sales email', type: 'email', hint: 'Demo, quotation and sales enquiries are routed here.' },
      { name: 'careers_email', label: 'Careers email', type: 'email', hint: 'Job applications are notified to this address.' },
      { name: 'support_email', label: 'Support email', type: 'email' },
      { name: 'primary_phone', label: 'Primary phone', type: 'text' },
      { name: 'secondary_phone', label: 'Secondary phone', type: 'text' },
      { name: 'address_lines', label: 'Address', type: 'textarea', hint: 'One line per row.' },
      { name: 'city', label: 'City', type: 'text' },
      { name: 'state', label: 'State', type: 'text' },
      { name: 'postal_code', label: 'Postal code', type: 'text' },
      { name: 'country', label: 'Country', type: 'text' },
      { name: 'map_embed_url', label: 'Map embed URL', type: 'url', hint: 'Shown on the contact page when set.' },
    ],
  },
  {
    key: 'social',
    label: 'Social',
    description: 'Profile links for the footer and the Organization structured data.',
    fields: [
      { name: 'linkedin_url', label: 'LinkedIn', type: 'url', missingOnSource: true },
      { name: 'twitter_url', label: 'X / Twitter', type: 'url', missingOnSource: true },
      { name: 'youtube_url', label: 'YouTube', type: 'url', missingOnSource: true },
      { name: 'github_url', label: 'GitHub', type: 'url', missingOnSource: true },
      { name: 'facebook_url', label: 'Facebook', type: 'url', missingOnSource: true },
      { name: 'instagram_url', label: 'Instagram', type: 'url', missingOnSource: true },
    ],
  },
  {
    key: 'seo',
    label: 'SEO',
    description: 'Defaults used wherever a page does not set its own metadata.',
    fields: [
      { name: 'default_seo_title', label: 'Default title', type: 'text' },
      { name: 'default_seo_description', label: 'Default meta description', type: 'textarea' },
      { name: 'default_seo_keywords', label: 'Default keywords', type: 'text' },
      { name: 'seo_title_suffix', label: 'Title suffix', type: 'text', hint: 'Appended to page titles, e.g. “ | Shyena Solutions”.' },
      { name: 'google_site_verification', label: 'Search Console verification token', type: 'text' },
      { name: 'robots_txt_extra', label: 'Extra robots.txt rules', type: 'textarea', hint: 'Appended to the generated robots.txt.' },
    ],
  },
  {
    key: 'copy',
    label: 'Copy & CTAs',
    description: 'Footer text and the call-to-action labels used across the site.',
    fields: [
      { name: 'footer_about', label: 'Footer description', type: 'textarea' },
      { name: 'copyright_text', label: 'Copyright line', type: 'text' },
      { name: 'newsletter_heading', label: 'Newsletter heading', type: 'text' },
      { name: 'newsletter_body', label: 'Newsletter description', type: 'textarea' },
      { name: 'primary_cta_label', label: 'Primary CTA label', type: 'text' },
      { name: 'primary_cta_url', label: 'Primary CTA URL', type: 'text' },
      { name: 'secondary_cta_label', label: 'Secondary CTA label', type: 'text' },
      { name: 'secondary_cta_url', label: 'Secondary CTA URL', type: 'text' },
      { name: 'demo_cta_label', label: 'Demo CTA label', type: 'text' },
      { name: 'quote_cta_label', label: 'Quotation CTA label', type: 'text' },
    ],
  },
  {
    key: 'features',
    label: 'Features',
    description: 'Switch whole sections of the website on or off.',
    fields: [
      { name: 'enable_blog', label: 'Blog', type: 'checkbox' },
      { name: 'enable_news', label: 'News', type: 'checkbox' },
      { name: 'enable_case_studies', label: 'Case studies', type: 'checkbox' },
      { name: 'enable_careers', label: 'Careers', type: 'checkbox' },
      { name: 'enable_newsletter', label: 'Newsletter signup', type: 'checkbox' },
      { name: 'enable_gated_downloads', label: 'Gated downloads (ask for details before a download)', type: 'checkbox' },
      { name: 'maintenance_mode', label: 'Maintenance mode (also blocks crawlers in robots.txt)', type: 'checkbox' },
      { name: 'maintenance_message', label: 'Maintenance banner message', type: 'textarea' },
    ],
  },
];

export function SettingsPage() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState('');

  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ['cms', 'settings'], queryFn: cmsApi.settings });

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => cmsApi.updateSettings(payload),
    onSuccess: () => {
      setErrors({});
      setNotice('Settings saved. The website updates immediately.');
      void queryClient.invalidateQueries({ queryKey: ['site', 'settings'] });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.isValidation) {
        setErrors(error.fields);
        setNotice('');
      } else {
        setNotice('');
        setErrors({ __all__: error instanceof ApiError ? error.message : 'Saving failed.' });
      }
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner label="Loading settings" />
      </div>
    );
  }
  if (isError) return <LoadError onRetry={() => void refetch()} />;

  const missingCount = GROUPS.flatMap((group) => group.fields).filter(
    (field) => field.missingOnSource && !form[field.name],
  ).length;

  function submit() {
    const payload: Record<string, unknown> = {};
    for (const field of GROUPS.flatMap((group) => group.fields)) {
      if (field.name in form) payload[field.name] = form[field.name];
    }
    mutation.mutate(payload);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">Site settings</h1>
          <p className="mt-1 text-sm text-muted">
            Everything here appears on the public website. No deployment is needed for a change to take effect.
          </p>
        </div>
        <Button onClick={submit} loading={mutation.isPending}>
          Save settings
        </Button>
      </div>

      {notice && <Alert tone="success">{notice}</Alert>}
      {errors.__all__ && <Alert tone="danger">{errors.__all__}</Alert>}

      {missingCount > 0 && (
        <Alert tone="warning" title={`${missingCount} field${missingCount === 1 ? '' : 's'} still to supply`}>
          Fields marked <em>not on the previous site</em> could not be recovered from <code>www.shyena.in</code> because
          it does not publish them. Filling them in completes the site's identity, structured data and lead routing.
        </Alert>
      )}

      <Card>
        <Tabs label="Settings groups" items={GROUPS.map((group) => ({ value: group.key, label: group.label }))}>
          {GROUPS.map((group) => (
            <TabPanel key={group.key} value={group.key}>
              <p className="mb-6 text-sm text-muted">{group.description}</p>
              <div className="grid gap-5 sm:grid-cols-2">
                {group.fields.map((field) => {
                  const id = `setting-${field.name}`;
                  const value = form[field.name];

                  if (field.type === 'checkbox') {
                    return (
                      <div key={field.name} className="sm:col-span-2">
                        <Checkbox
                          id={id}
                          label={field.label}
                          checked={Boolean(value)}
                          onChange={(event) => setForm({ ...form, [field.name]: event.target.checked })}
                        />
                      </div>
                    );
                  }

                  const hint = [field.hint, field.missingOnSource && !value ? 'Not on the previous site.' : '']
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <div key={field.name} className={field.type === 'textarea' ? 'sm:col-span-2' : undefined}>
                      <FormField
                        label={field.label}
                        htmlFor={id}
                        {...(hint ? { hint } : {})}
                        {...(errors[field.name] ? { error: errors[field.name] } : {})}
                      >
                        {field.type === 'textarea' ? (
                          <Textarea
                            id={id}
                            rows={3}
                            value={String(value ?? '')}
                            onChange={(event) => setForm({ ...form, [field.name]: event.target.value })}
                            error={Boolean(errors[field.name])}
                          />
                        ) : (
                          <Input
                            id={id}
                            type={field.type === 'date' ? 'date' : field.type}
                            value={String(value ?? '')}
                            onChange={(event) => setForm({ ...form, [field.name]: event.target.value })}
                            error={Boolean(errors[field.name])}
                          />
                        )}
                      </FormField>
                    </div>
                  );
                })}
              </div>
            </TabPanel>
          ))}
        </Tabs>
      </Card>

      <Card>
        <h2 className="text-base font-semibold text-ink">Logo, favicon and analytics</h2>
        <p className="mt-2 text-sm text-muted">
          The logo and favicon are media assets: upload them in the{' '}
          <a href="/admin/media">media library</a> and select them here once uploaded. Analytics providers (Google
          Analytics, Tag Manager, Search Console, LinkedIn) are configured as records so an ID can be added without a
          deployment — the previous website had none.
        </p>
      </Card>
    </div>
  );
}
